import MailPiecePreview from "../../../../modules/createEditor/components/MailPiecePreview";
import type { MailPieceSize, QrPlacement } from "../../../../modules/createEditor/mailPieceTemplates";
import type { AdCardRendererProps } from "../types";

/*
    A mail piece on a card (MPN-1706, MPN-1948, MPN-1949).

    Without this, a postcard renders as "[direct_mail renderer not implemented]", which is
    what a reviewer would have seen while being asked to approve it.

    The artwork is HTML the printer renders, so the preview is that same HTML in a sandboxed
    frame: no scripts, no navigation, no same-origin access. It is client copy from a
    database, and it is about to be printed on paper, so it is shown rather than described,
    but it is never trusted.

    BOTH SIDES, LABELLED (MPN-1949). This used to draw the front alone, on a client-facing
    review screen whose entire purpose is approval. A postcard is two-sided, so approving one
    on the strength of its front is approving something nobody has read -- and the back is the
    side carrying the offer, the QR code and the address block.

    NOTHING IS CACHED (MPN-1948). That ticket asks for a render function so a stored thumbnail
    can be refreshed after an edit. There is no stored thumbnail: this renders `front_html`
    and `back_html`, which are the same strings Lob prints. A card cannot show a stale version
    of itself because there is no second copy to go stale. What was stale was the resolve
    call -- see AdCard.

    THE ASPECT RATIO COMES FROM THE SIZE. It was hardcoded to 6/4, so a 6x9 and a 6x11 were
    both drawn as though they were 4x6 -- squashed, and misleading about how much room the
    copy actually has.

    Merge fields stay as written. `{{first_name}}` is what the card says until Lob fills it
    in per person, and quietly substituting a sample name here would hide an unfilled field
    from the only person who could catch it.

    THE QR CODE AND THE ADDRESS BLOCK ARE DRAWN, NOT IN THE HTML (MPN-1949). Neither is in
    `back_html`: Lob stamps the address, barcode and indicia onto the back after rendering our
    artwork, and generates the QR from a parameter. So the artwork alone shows a card with two
    blank corners, and Rachel asked what was going to fill them -- which is the right question
    to ask before approving a card. This now renders through `MailPiecePreview`, the same
    component the editor uses, so the client sees what the author saw and both are drawn from
    the one copy of Lob's geometry.

    Two things differ from the editor's use of it, and both are deliberate: no print guides
    (trim and safe-copy area are production concerns, not approval ones), and no sample data
    (see above).
*/

/** Finished-card proportions, for the list thumbnail. A postcard is landscape on every size. */
const ASPECT: Record<string, string> = {
    "4x6": "6 / 4",
    "6x9": "9 / 6",
    "6x11": "11 / 6",
};

const SIZES: MailPieceSize[] = ["4x6", "6x9", "6x11"];

/** The stored size, or the default. Narrowed here because `content` is an open dict. */
function mailSize(size?: string): MailPieceSize {
    return SIZES.includes(size as MailPieceSize) ? (size as MailPieceSize) : "4x6";
}

/**
 * The stored placement, or null for the platform default.
 *
 * Null is not "no code" -- it is every card authored before placement was editable, which is
 * most of them. `MailPiecePreview` resolves it to where those cards actually print, so an
 * un-placed code is still drawn in the right square.
 */
function qrPlacement(content?: {
    qr_left?: number | null; qr_bottom?: number | null; qr_width?: number | null;
}): QrPlacement | undefined {
    const { qr_left, qr_bottom, qr_width } = content ?? {};
    if (qr_left == null || qr_bottom == null || qr_width == null) return undefined;
    return { left: qr_left, bottom: qr_bottom, width: qr_width };
}

function Side({ label, html, title, size, placement }: {
    label: string; html?: string; title: string;
    size: MailPieceSize; placement?: QrPlacement;
}) {
    return (
        <figure className="adcard-mail__side">
            <div className="adcard-mail__frame">
                <MailPiecePreview
                    size={size}
                    html={html ?? ""}
                    side={label.toLowerCase() as "front" | "back"}
                    title={title}
                    qrPlacement={placement}
                    /*
                        No print guides. The trim line and the safe-copy area are production
                        concerns for whoever lays the card out; a client approving it is being
                        asked whether the card is right, and three annotation rectangles across
                        the artwork answer a question they were not asked. The address block and
                        the QR still draw -- those are on the card, not notes about it.
                    */
                    showGuides={false}
                    /*
                        Merge fields stay as written here, unlike in the editor. `{{first_name}}`
                        left alone is how the person approving the card can notice a field
                        nobody wired up; a sample name makes an unfilled field look finished.
                    */
                    substituteSampleData={false}
                />
            </div>
            {/* Labelled even when a side is empty. "Which one am I looking at" is the
                question a two-up layout creates, and an unlabelled blank is worse than a
                labelled one. */}
            <figcaption className="adcard-mail__label">{label}</figcaption>
        </figure>
    );
}

export default function AdCardDirectMail({ ad, mode }: AdCardRendererProps) {
    const content = ad.content as
        | {
            front_html?: string; back_html?: string; qr_destination?: string; size?: string;
            qr_left?: number | null; qr_bottom?: number | null; qr_width?: number | null;
        }
        | undefined;

    const front = content?.front_html?.trim();
    const back = content?.back_html?.trim();
    const size = content?.size ?? "4x6";
    const aspect = ASPECT[size] ?? ASPECT["4x6"];
    const qr = content?.qr_destination?.trim();
    const cardSize = mailSize(content?.size);
    const placement = qrPlacement(content);

    // Compact is a thumbnail in a list; two sides there would be two illegible smears. The
    // front is the identifying side, so that is the one a thumbnail shows.
    const bothSides = mode !== "compact";

    return (
        <>
            {/* --mail lifts the three-line text cap. `.adcard-media__copy` is sized for a
                caption (78px = 3 lines + padding) and this holds artwork, so without the
                modifier both sides are cut off mid-card whatever the dialog's width. Compact
                keeps the cap on purpose: that one is a list thumbnail and a full-height
                postcard in a table row is a different bug. */}
            <div className={`adcard-media__copy${bothSides ? " adcard-media__copy--mail" : ""}`}>
                {bothSides ? (
                    <div className="adcard-mail__sides">
                        <Side label="Front" html={front} size={cardSize}
                            title={`${ad.name ?? "Mail piece"} front`} />
                        <Side label="Back" html={back} size={cardSize} placement={placement}
                            title={`${ad.name ?? "Mail piece"} back`} />
                    </div>
                ) : front ? (
                    <iframe
                        title={`${ad.name ?? "Mail piece"} front`}
                        srcDoc={front}
                        sandbox=""
                        scrolling="no"
                        style={{
                            width: "100%", aspectRatio: aspect,
                            border: "1px solid #e1e6ef", borderRadius: 6, background: "#fff",
                        }}
                    />
                ) : (
                    <div className="adcard-media__copy-empty">No artwork yet</div>
                )}
            </div>

            {mode !== "compact" && (
                <div className="adcard-media__footer">
                    <div className="adcard-media__footer-text">
                        <div className="adcard-media__headline">{size} postcard</div>
                        <div className="adcard-media__desc">
                            {qr
                                ? qr.includes("{{code}}")
                                    ? "QR code, unique per recipient"
                                    : "QR code, the same link on every card"
                                : "No QR code"}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
